import { useState } from 'react';
import ConditionalRender from '../../helpers/ConditionalRender';
import './style.scss';

export interface FilterOption {
    label: string;
    value: string;
}

export interface FilterGroup {
    name: string;
    label: string;
    options: FilterOption[] | string[];
    selectedValues: string[];
    onSelectionChange: (values: string[]) => void;
}

export interface FilterPanelProps {
    filterGroups: FilterGroup[];
    onClearAll?: () => void;
    isCollapsible?: boolean;
    isLoading?: boolean;
}

function FilterPanel({
    filterGroups,
    onClearAll,
    isCollapsible = true,
    isLoading = false
}: FilterPanelProps) {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const createBadgeValue = (category: string, values: string[]): string => {
        if (values.length === 0) return '';
        return `${category}=${values.join(', ')}`;
    };

    const clearFilter = (group: FilterGroup) => {
        group.onSelectionChange([]);
    };

    const handleClearAll = () => {
        if (onClearAll) {
            onClearAll();
        } else {
            filterGroups.forEach(group => clearFilter(group));
        }
    };

    return (
        <div className="card mb-3 filter-panel">
            <div className="card-header">
                <i className="fal fa-filter fa-fw" aria-hidden="true"/>
                Filter By
                {isCollapsible && (
                    <i className="collapse-toggle collapsed"
                       role="button" 
                       onClick={() => setIsCollapsed(!isCollapsed)}
                       data-bs-toggle="collapse"
                       data-bs-target="#collapseFilters"
                       aria-expanded={!isCollapsed}
                       aria-controls="collapseFilters"
                    />
                )}
                <div className="d-inline-block ms-2">
                    {filterGroups.map((group, index) => (
                        group.selectedValues.length > 0 && (
                            <div key={index} className="filter-badge-wrapper badge bg-success text-white">
                                <span className="filter-badge-content">
                                    {createBadgeValue(group.label, group.selectedValues)}
                                </span>
                                <i 
                                    className="fal fa-times-circle filter-clear-button" 
                                    onClick={() => clearFilter(group)} 
                                    title={`Clear ${group.label} Filter`}
                                />
                            </div>
                        )
                    ))}
                </div>
            </div>
            <div id="collapseFilters" className={`collapse ${!isCollapsed || !isCollapsible ? 'show' : ''}`}>
                <div className="card-body">
                    <div className="d-flex">
                        <div className="filter-groups">
                            {filterGroups.map((group, groupIndex) => (
                                <div key={groupIndex} className="btn-group dropdown">
                                    <button 
                                        type="button" 
                                        className="btn btn-dropdown filter-group-button"
                                        data-bs-toggle="dropdown" 
                                        aria-expanded="false"
                                        disabled={isLoading}
                                    >
                                        {group.label}
                                        <ConditionalRender if={group.selectedValues.length > 0}>
                                            <span className="badge bg-info" data-tip="Applied filters count">
                                                {group.selectedValues.length}
                                            </span>
                                        </ConditionalRender>
                                    </button>
                                    <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                        <div className="dropdown-menu-scroll">
                                            {group.options.map((option, optionIndex) => {
                                                const optionValue = typeof option === 'string' ? option : option.value;
                                                const optionLabel = typeof option === 'string' ? option : option.label;
                                                
                                                return (
                                                    <li key={optionIndex}
                                                        onClick={() => {
                                                            if (isLoading) return;
                                                            const newValues = group.selectedValues.includes(optionValue)
                                                                ? group.selectedValues.filter(v => v !== optionValue)
                                                                : [...group.selectedValues, optionValue];
                                                            group.onSelectionChange(newValues);
                                                        }}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={group.selectedValues.includes(optionValue)}
                                                            disabled={isLoading}
                                                            readOnly
                                                        />
                                                        <span title={optionLabel}>{optionLabel}</span>
                                                    </li>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button 
                            className="btn btn-sm btn-dark btn-reset" 
                            onClick={handleClearAll}
                            disabled={isLoading}
                        >
                            <i className="far fa-undo"/>
                            <span>Clear</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FilterPanel; 